import { makeAutoObservable } from 'mobx'
import { RootStore } from 'stores'
import {
  getMyAssignments,
  getPlayerAssignmentOverview,
  getPlayerWeekDetail,
  getPlayerStationDetail,
  updateDrillCompletion,
  submitDrillMetrics,
  submitDrillVideo,
  IToggleDrillCompletionResponse
} from 'API/player-assignment'
import { uploadFile } from 'API/cms'
import {
  IPlayerAssignment,
  IPlayerAssignmentSearchFilter,
  IPlayerAssignmentOverview,
  IPlayerWeekDetailResponse,
  IPlayerStationDetailResponse
} from 'interfaces/player-assignment'

class PlayerAssignmentStore {
  rootStore: RootStore
  assignments: IPlayerAssignment[] = []
  recentAssignments: IPlayerAssignment[] = []
  currentOverview: IPlayerAssignmentOverview | null = null
  currentWeekDetail: IPlayerWeekDetailResponse | null = null
  currentStationDetail: IPlayerStationDetailResponse | null = null
  totalCount: number = 0
  isLoadingList: boolean = false
  isLoadingDetail: boolean = false

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore
    makeAutoObservable(this)
  }

  async fetchMyAssignments(filter: IPlayerAssignmentSearchFilter = {}): Promise<void> {
    this.isLoadingList = true
    try {
      const { results, totalCount } = await getMyAssignments(filter)
      this.assignments = results
      this.totalCount = totalCount
      const hasSearch = Boolean(filter.search?.trim())
      if (!hasSearch && (!filter.page || filter.page === 1)) {
        // "Recently" = the most recently updated assignments, newest first (leftmost).
        this.recentAssignments = results
          .slice()
          .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
          .slice(0, 5)
      }
    } catch (error) {
      console.error('Failed to fetch assignments:', error)
      this.assignments = []
      this.totalCount = 0
    } finally {
      this.isLoadingList = false
    }
  }

  async fetchAssignmentOverview(assignmentId: number): Promise<void> {
    this.isLoadingDetail = true
    // Clear stale data so the header/title never shows the previous assignment
    // while the new one is loading.
    this.currentOverview = null
    try {
      this.currentOverview = await getPlayerAssignmentOverview(assignmentId)
    } finally {
      this.isLoadingDetail = false
    }
  }

  async fetchWeekDetail(assignmentId: number, weekId: number): Promise<void> {
    this.isLoadingDetail = true
    this.currentWeekDetail = null
    try {
      this.currentWeekDetail = await getPlayerWeekDetail(assignmentId, weekId)
    } finally {
      this.isLoadingDetail = false
    }
  }

  async fetchStationDetail(assignmentId: number, stationId: number): Promise<void> {
    this.isLoadingDetail = true
    this.currentStationDetail = null
    try {
      this.currentStationDetail = await getPlayerStationDetail(assignmentId, stationId)
    } finally {
      this.isLoadingDetail = false
    }
  }

  async toggleDrillCompletion(
    assignmentId: number,
    drillId: number,
    isCompleted: boolean
  ): Promise<IToggleDrillCompletionResponse> {
    const result = await updateDrillCompletion(assignmentId, drillId, isCompleted)
    if (this.currentStationDetail?.station?.drills) {
      const drill = this.currentStationDetail.station.drills.find(d => d._id === drillId)
      if (drill) {
        Object.assign(drill, { isCompleted })
        // Recalculate station-level counters
        const drills = this.currentStationDetail.station.drills
        const completedCount = drills.filter(d => d.isCompleted).length
        this.currentStationDetail.station.completedDrillCount = completedCount
        this.currentStationDetail.station.isCompleted = drills.length > 0 && completedCount === drills.length
      }
    }
    return result
  }

  async submitMetrics(
    assignmentId: number,
    drillId: number,
    metrics: Array<{ metricId: number; value: string | number }>
  ): Promise<void> {
    await submitDrillMetrics(assignmentId, drillId, metrics)
  }

  async submitVideoMetric(
    organizationId: number,
    assignmentId: number,
    drillId: number,
    file: File | undefined,
    note?: string,
    existingVideoFileName?: string
  ): Promise<void> {
    let videoFileName = existingVideoFileName
    if (file) {
      // /cms/video requires organizationId as a mandatory query param.
      const uploadedVideoUrl = await uploadFile('video', file, undefined, organizationId)
      videoFileName = this.extractFileName(uploadedVideoUrl)
    }
    await submitDrillVideo(assignmentId, drillId, videoFileName, note)
  }

  private extractFileName(value?: string): string {
    const normalizedValue = String(value ?? '').trim()
    if (!normalizedValue) return ''

    try {
      const parsed = new URL(normalizedValue)
      return decodeURIComponent(parsed.pathname.split('/').filter(Boolean).pop() ?? '')
    } catch {
      return normalizedValue
    }
  }

  reset(): void {
    this.assignments = []
    this.recentAssignments = []
    this.currentOverview = null
    this.currentWeekDetail = null
    this.currentStationDetail = null
    this.totalCount = 0
    this.isLoadingList = false
    this.isLoadingDetail = false
  }
}

export default PlayerAssignmentStore
